<template>
  <PortfolioFormLayout :loading="loading || loadingDependencies">
    <template #header>
      <h5 class="my-2">{{ isUpdateForm ? 'Update' : 'Create' }} Product Component</h5>
    </template>
    <template #body>
      <BootstrapAlert v-if="fatalError" variant="danger">{{ fatalError }}</BootstrapAlert>
      <ProductComponentFormPresentational
        v-model:component-type="componentType"
        v-model:cost-pool="costPool"
        v-model:description="description"
        v-model:direct-cost-identifier="directCostIdentifier"
        v-model:direct-cost-weight="directCostWeight"
        v-model:direct-cost-weight-description="directCostWeightDescription"
        v-model:estimated-cost-units="estimatedCostUnits"
        v-model:estimated-cost-description="estimatedCostDescription"
        v-model:name="name"
        v-model:oem-can-access-data="oemCanAccessData"
        v-model:personal-data-stored="personalDataStored"
        v-model:product-offering="productOffering"
        v-model:reseller-can-access-data="resellerCanAccessData"
        :component-type-options="componentTypeOptions"
        :cost-pool-options="costPoolOptions"
        :cost-pool-type="costPoolType"
        :product-offering-options="productOfferingOptions"
        :error-messages="errorMessages"
        :loading="loadingSubmit"
        :server-error="serverError"
        :disabled="!!fatalError"
        @submit="submit"
      />
    </template>
  </PortfolioFormLayout>
</template>
<script lang="ts" setup>
import {Detail} from '@/api/errorMessage';
import {ProductComponentCreateUpdate} from '@/stores/userManagement/userManagement';

const props = defineProps<{
  isUpdateForm: boolean;
  loading: boolean;
  id?: number;
}>();

const router = useRouter();

// Stores
const productComponentsStore = useProductComponentsStore();
const productsStore = useProductsStore();
const componentTypesStore = useComponentTypesStore();
const costPoolsStore = useCostPoolsStore();

// Store refs
const getProductComponents = computed(() => productComponentsStore.getProductComponents);
const getProductOfferings = computed(() => productsStore.getProductOfferings);
const getComponentTypes = computed(() => componentTypesStore.getComponentTypes);
const getCostPools = computed(() => costPoolsStore.getCostPools);

const loadingSubmit = ref(false);
const loadingDependencies = ref(true);

// Used to store errors that are never supposed to happen
const fatalError = ref<string | undefined>();
const fatal = (error: string): Error => {
  fatalError.value = error;
  return Error(`Fatal error: ${error}`);
};

// Form data
const name = ref('');
const description = ref('');
const componentType = ref('');
const componentTypeOptions = computed(() => getComponentTypes.value.map((ct) => ct.name));
const costPool = ref('');
const costPoolOptions = computed(() => getCostPools.value.map((cp) => cp.name));
const costPoolType = computed(
  () => getCostPools.value?.find((cp) => cp.name === costPool.value)?.type || '',
);
const productOffering = ref('');
const productOfferingOptions = computed(() => getProductOfferings.value.map((po) => po.name));
// Direct Cost
const directCostIdentifier = ref('');
const directCostWeight = ref(0);
const directCostWeightDescription = ref('');
// Indirect Cost
const estimatedCostUnits = ref(0);
const estimatedCostDescription = ref('');
// GDPR
const personalDataStored = ref(false);
const oemCanAccessData = ref(false);
const resellerCanAccessData = ref(false);
const errorMessages = ref(new Map());
const serverError = ref(false);

const submit = async (form: globalThis.Ref<HTMLFormElement | null>) => {
  try {
    loadingSubmit.value = true;

    let type_id = getComponentTypes.value?.find(({name}) => name === componentType.value)?.id;
    let pool_id = getCostPools.value?.find(({name}) => name === costPool.value)?.id;

    if (!type_id) throw fatal('Missing component type');
    if (!pool_id) throw fatal('Missing cost pool');

    let component: ProductComponentCreateUpdate = {
      name: name.value,
      description: description.value,
      component_type_id: type_id,
      cost_pool_id: pool_id,
      personal_data_stored: personalDataStored.value,
      oem_can_access_data: oemCanAccessData.value,
      reseller_can_access_data: resellerCanAccessData.value,
      direct_product_offering_id: getProductOfferings.value?.find(
        ({name}) => name === productOffering.value,
      )?.id,
    };
    if (costPoolType.value === 'indirect') {
      component = {
        ...component,
        estimated_cost_units: estimatedCostUnits.value,
        estimated_cost_description: estimatedCostDescription.value,
      };
    } else if (costPoolType.value === 'direct') {
      component = {
        ...component,
        direct_cost_identifier: directCostIdentifier.value,
        direct_cost_weight: directCostWeight.value,
        direct_cost_weight_description: directCostWeightDescription.value,
      };
    }
    if (!props.isUpdateForm) {
      await productComponentsStore.createProductComponent(component);
      router.go(-1);
      // Clear the form because we cache it using Keep Alive.
    } else {
      const updateComponent: ProductComponentCreateUpdate = {
        ...component,
        id: Number(props.id),
      };
      await productComponentsStore.updateProductComponent(updateComponent);
      router.go(-1);
    }
  } catch (errorMessage: any) {
    serverError.value = true;
    errorMessages.value = new Map();
    // Remove .was-validated for server-side validation response.
    form.value?.classList.remove('was-validated');
    if (typeof errorMessage !== 'string' && errorMessage.length > 0) {
      errorMessage.forEach((d: Detail) => errorMessages.value.set(d.loc[1], d.msg));
    }
    form.value?.classList.remove('was-validated');
  } finally {
    loadingSubmit.value = false;
  }
};

// TODO: RND-1374
const fetchData = async () => {
  try {
    await Promise.all([
      productsStore.readProductOfferings({limit: 500}),
      componentTypesStore.readComponentTypes({limit: 500}),
      costPoolsStore.readCostPools({limit: 500}),
    ]);
  } catch (errorMessage) {
    console.log(errorMessage);
  }
};

const load = async () => {
  await fetchData();
  if (props.isUpdateForm === true && props.id !== undefined) {
    // Try find the component in store state
    let foundPc = getProductComponents.value.find((pc) => pc.id === props.id);
    // If the item is missing, try to fetch it
    if (foundPc === undefined) {
      try {
        const data = await productComponentsStore.readProductComponentById(props.id);
        foundPc = data;
      } catch (error) {
        console.log(error);
      }
    }
    // If the item is found, try populate the form
    if (foundPc !== undefined) {
      const foundCt = getComponentTypes.value?.find((ct) => ct.id === foundPc?.component_type_id);
      const foundCp = getCostPools.value?.find((cp) => cp.id === foundPc?.cost_pool_id);
      const foundPo = getProductOfferings.value?.find(
        (po) => po.id === foundPc?.direct_product_offering_id,
      );
      if (foundCt && foundCp && foundPo) {
        name.value = foundPc.name;
        description.value = foundPc.description;
        directCostIdentifier.value = foundPc.direct_cost_identifier || '';
        directCostWeight.value = Number(foundPc.direct_cost_weight) || 0;
        directCostWeightDescription.value = foundPc.direct_cost_weight_description || '';
        estimatedCostUnits.value = Number(foundPc.estimated_cost_units) || 0;
        estimatedCostDescription.value = foundPc.estimated_cost_description || '';
        personalDataStored.value = foundPc.personal_data_stored;
        oemCanAccessData.value = foundPc.oem_can_access_data;
        resellerCanAccessData.value = foundPc.reseller_can_access_data;
        componentType.value = foundCt.name;
        costPool.value = foundCp.name;
        productOffering.value = foundPo.name;
      }
    }
  }
  loadingDependencies.value = false;
};

onMounted(() => load());
</script>
